import { createContext, ReactNode, useEffect, useRef, useState } from "react";
import { LoadingScreen } from "@/components";
import { useLocation } from "react-router";

interface ILoadingContext {
  visible: boolean;
  show: () => void;
  hide: (immediate?: boolean) => void;
}

export const LoadingContext = createContext<ILoadingContext>({} as ILoadingContext);

// Prevents screen flicker if loading happens quickly.
const MIN_VISIBLE_TIME = 500;

export function LoadingProvider(props: { children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const startTimeRef = useRef<number>(0);
  const location = useLocation();

  // Hide loader when the URL changes.
  useEffect(() => {
    setVisible(false);
    document.body.style.overflow = "unset";
  }, [location.pathname]);

  function show() {
    setVisible(true);
    startTimeRef.current = Date.now();

    // Hide scrollbars.
    document.body.style.overflow = "hidden";
  }

  function hide(immediate?: boolean) {
    const elapsedTime = Date.now() - startTimeRef.current;
    const remainingTime = immediate ? 0 : Math.max(0, MIN_VISIBLE_TIME - elapsedTime);

    setTimeout(() => {
      setVisible(false);
      // Restore scrollbars.
      document.body.style.overflow = "unset";
    }, remainingTime);
  }

  return (
    <LoadingContext.Provider value={{ visible, show, hide }}>
      {visible && <LoadingScreen />}
      {props.children}
    </LoadingContext.Provider>
  );
}
